import dayjs from "dayjs";
import {
  IFreshCreateOrUpdateCatData,
} from "libs/api/cats/src/lib/types/abandoned-cart/cat-create-or-update.types";
import {
  IFreshCreateOrUpdateCatsRequest,
  IFreshCustomerSUFAnswers,
} from "@api-clients/cats";
import { ICatRecipeDetails } from "@api-clients/checkout";
import {
  calculateSuggestedCalories,
  calculateSuggestedCaloriesFromBirthday,
  calculateTraySize,
  getAgeAtSignup,
} from "@./cat-calculations";

import { CatLifeStage } from "@/primary-models";

import {
  PricingPlan,
  nibblesProductTitleEnum,
  type TCatFormDataInput,
  type TRecipeItem,
  type TSubscriptionData,
  type TUserFormData,
} from "@./suf-store";

const DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_CAT_WEIGHT_GRAMS = 4000;
const DEFAULT_CAT_BIRTHDAY = dayjs().format(DATE_FORMAT);

/**
 * Calculates the standardised daily calories for a cat based on their data.
 * Returns the standardised calorie value from the tray size mapping.
 * Direct sales never asks whether the cat is neutered, so those cats are treated as neutered.
 */
export function calculateCaloriesForCat(cat: TCatFormDataInput): number | undefined {
  if (!cat.weight?.valueInKg || !cat.body?.bodyType || !cat.body?.activityLevel) {
    return undefined;
  }

  const catProfile = { breed: cat.breed, gender: cat.gender };

  let suggestedCalories = undefined;
  if (cat.birthday) {
    suggestedCalories = calculateSuggestedCaloriesFromBirthday(
      cat.weight.valueInKg,
      cat.body.bodyType,
      cat.body.activityLevel,
      dayjs(cat.birthday),
      catProfile,
      cat.isNeutered,
    );
  } else if (cat.age) {
    suggestedCalories = calculateSuggestedCalories(
      cat.weight.valueInKg,
      cat.body.bodyType,
      cat.body.activityLevel,
      cat.age,
      catProfile,
      cat.isNeutered,
    );
  }

  if (!suggestedCalories) {
    return undefined;
  }

  const mealPlan = calculateTraySize(suggestedCalories);
  return mealPlan.calories;
}

/**
 * Recomputes calculatedCalories for rehydrated cats that already hold one, so a cart with
 * legacy body condition and activity level values can't carry a stale recommendation.
 * Cats without a figure yet are left alone.
 */
export function recalculateCatCalories<T extends TCatFormDataInput>(
  cats: Record<string, T>,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(cats).map(([ id, cat ]) => {
      if (cat.calculatedCalories === undefined) {
        return [ id, cat ];
      }
      const calculatedCalories = calculateCaloriesForCat(cat);
      return [ id, calculatedCalories === undefined ? cat : { ...cat, calculatedCalories } ];
    }),
  );
}

export const transformRecipesForApi = (cat: TCatFormDataInput): Record<string, number> => {
  const recipeMap: Record<string, number> = {};
  cat.selectedRecipes?.recipes?.forEach((recipe: TRecipeItem) => {
    if (recipe.type) {
      recipeMap[recipe.type] = (recipeMap[recipe.type] || 0) + (recipe.quantity ?? 0);
    }
  });
  return recipeMap;
};

export const getHasAddedNibblesTrioBundle = (
  upsell: TSubscriptionData["nibblesUpsell"] | undefined,
): boolean => upsell?.added === true && upsell.type === nibblesProductTitleEnum.enum.NIBBLES_TRIO_BUNDLE;

const buildCatAge = (lifeStage: CatLifeStage, age: TCatFormDataInput["age"]) => ({
  years: lifeStage === CatLifeStage.KITTEN ? undefined : age?.years || undefined,
  months: lifeStage === CatLifeStage.KITTEN ? age?.months || undefined : undefined,
  weeks: lifeStage === CatLifeStage.KITTEN ? age?.weeks || undefined : undefined,
});

const buildCatRecipeDetail = (
  cat: TCatFormDataInput,
  index: number,
  context: {
    hasTrioBundle: boolean;
    trialMealPlan: ICatRecipeDetails["trialMealPlan"];
    ongoingMealPlan: ICatRecipeDetails["ongoingMealPlan"];
  },
): ICatRecipeDetails => {
  const isPrimary = index === 0;
  const lifeStage = cat.lifeStage ? cat.lifeStage.toUpperCase() as CatLifeStage : CatLifeStage.ADULT;
  return {
    catName: cat.name ?? "",
    sprinklesQuantity: cat.freeGiftTreat?.added ? 1 : 0, // note this is not actually being set in the store and will be removed in SUFv3
    hasAddedNibblesTrioBundle: isPrimary && context.hasTrioBundle,
    recipeTitleToQuantity: transformRecipesForApi(cat),
    trialMealPlan: context.trialMealPlan,
    ongoingMealPlan: context.ongoingMealPlan,
    weightInKg: cat.weight?.valueInKg,
    bodyType: cat.body?.bodyType,
    lifestyle: cat.body?.activityLevel,
    isNeutered: cat.isNeutered,
    allergies: cat.diet?.foodAllergies?.allergens ?? [],
    catAge: buildCatAge(lifeStage, cat.age),
    breed: cat.breed,
    gender: cat.gender,
  };
};

export const transformCatDataForApi = (
  cats: Record<string, TCatFormDataInput>,
  subscription: TSubscriptionData,
): ICatRecipeDetails[] => {
  const hasTrioBundle = getHasAddedNibblesTrioBundle(subscription.nibblesUpsell);
  const trialMealPlan = subscription.trial?.planType ?? PricingPlan.TRIAL;
  const ongoingMealPlan = subscription.ongoing?.planType ?? PricingPlan.STANDARD;

  return Object.values(cats)
    .filter((cat) => !cat.softDelete)
    .map((cat, index) => buildCatRecipeDetail(cat, index, {
      hasTrioBundle,
      trialMealPlan,
      ongoingMealPlan,
    }));
};

/**
 * Calculates birthday for kittens based on age in weeks or months.
 * Returns a rough date that will later be adjusted to user's preferred month.
 */
const calculateKittenBirthdayDate = (age: TCatFormDataInput["age"]): dayjs.Dayjs | undefined => {
  if (!age) { return undefined; }

  const ageMode = age.mode ?? "months";

  if (ageMode === "weeks" && age.weeks) {
    return dayjs().add(-age.weeks, "weeks");
  }

  if (ageMode === "months" && age.months) {
    return dayjs().add(-age.months, "months");
  }

  return undefined;
};

/**
 * Calculates birthday for adult/senior cats based on age in years.
 * Returns a rough date that will later be adjusted to user's preferred month.
 */
const calculateAdultBirthdayDate = (age: TCatFormDataInput["age"]): dayjs.Dayjs | undefined => {
  if (!age) { return undefined; }
  if (age.years) { return dayjs().add(-age.years, "years"); }
  return undefined;
};

/**
 * Applies the user's preferred birthday month to a calculated date.
 *
 * Example: If cat is 2 years old (calculated: March 15, 2022) and user
 * prefers JANUARY, result is January 1, 2022.
 *
 * Note: This function is only used for adult/senior cats. Kittens bypass this
 * and have their day standardized directly in calculateCatBirthday.
 *
 * @param date - The calculated birthday date from age calculation
 * @param birthdayMonth - User's preferred month (optional)
 * @returns Date with user's preferred month (or original month if undefined), same year, day set to 1st
 */
const applyBirthdayMonth = (date: dayjs.Dayjs, birthdayMonth?: TCatFormDataInput["birthdayMonth"]): dayjs.Dayjs => {
  if (!birthdayMonth) { return date; }

  const monthMap: Record<string, number> = {
    JANUARY: 0,
    FEBRUARY: 1,
    MARCH: 2,
    APRIL: 3,
    MAY: 4,
    JUNE: 5,
    JULY: 6,
    AUGUST: 7,
    SEPTEMBER: 8,
    OCTOBER: 9,
    NOVEMBER: 10,
    DECEMBER: 11,
  };

  const targetMonth = monthMap[birthdayMonth];
  if (targetMonth === undefined) { return date; }

  let result = date.month(targetMonth).date(1);

  // If applying the birthday month moves the date forward (later in the year),
  // we need to subtract a year to maintain the approximate age.
  if (result.isAfter(date)) {
    result = result.subtract(1, "year");
  }

  return result;
};

/**
 * Calculates the cat's birthday by combining age calculation with user's month preference.
 *
 * This allows us to get the correct YEAR from age calculation while respecting
 * the user's preference for the month.
 *
 * For kittens, the calculated month is preserved (not changed to birthdayMonth) but day is set to 1st.
 *
 * @param cat - The cat form data containing age, life stage, and optional birthday month
 * @returns The calculated birthday as YYYY-MM-DD string, or undefined if calculation fails
 */
export const calculateCatBirthday = (cat: TCatFormDataInput): string | undefined => {
  const { age, lifeStage, birthdayMonth } = cat;

  if (!age || !lifeStage) {
    return undefined;
  }

  let calculatedDate: dayjs.Dayjs | undefined;

  if (lifeStage === "kitten") {
    calculatedDate = calculateKittenBirthdayDate(age);
    return calculatedDate ? calculatedDate.date(1).format(DATE_FORMAT) : undefined;
  }
  if (lifeStage === "adult" || lifeStage === "senior") {
    calculatedDate = calculateAdultBirthdayDate(age);
  }

  if (!calculatedDate) {
    return undefined;
  }

  return applyBirthdayMonth(calculatedDate, birthdayMonth).format(DATE_FORMAT);
};

export interface ITransformStoreDataForCreateFreshCustomerParams {
  cats: Record<string, TCatFormDataInput>;
  user: TUserFormData;
  subscription: TSubscriptionData;
  userId?: string;
  formUsed: string;
  trackingInfo?: {
    fbc?: string;
    fbp?: string;
    pageUrl?: string;
    ttclid?: string;
    _ttp?: string;
  };
  isShreddedRecipeTestVariant?: boolean;
}

const buildCatDetails = (cat: TCatFormDataInput): IFreshCreateOrUpdateCatData["details"] => {
  const weightInGrams = cat.weight?.valueInKg ? cat.weight.valueInKg * 1000 : DEFAULT_CAT_WEIGHT_GRAMS;

  return {
    breed: cat.breed ?? "UNKNOWN",
    birthday: cat.birthday ?? calculateCatBirthday(cat) ?? DEFAULT_CAT_BIRTHDAY,
    isBirthdayPrecise: false,
    bodyType: cat.body?.bodyType ?? "UNKNOWN",
    activityLevel: cat.body?.activityLevel ?? "UNKNOWN",
    // Direct Sales never asks, and the calorie code treats a missing value as neutered.
    isNeutered: cat.isNeutered ?? true,
    weight: weightInGrams,
    allergies: cat.diet?.foodAllergies?.allergens ?? [],
    medicalConditions: cat.medical?.conditions ?? [],
  };
};

const transformCatForAbandonedCart = (cat: TCatFormDataInput): IFreshCreateOrUpdateCatData => ({
  cat: {
    name: cat.name ?? "",
    gender: cat.gender ?? "UNKNOWN",
  },
  details: buildCatDetails(cat),
  answers: {
    ageAtSignup: getAgeAtSignup({
      catLifeStage: cat.lifeStage ? cat.lifeStage.toUpperCase() as CatLifeStage : undefined,
      catAgeYears: cat.age?.years,
      catAgeMonths: cat.age?.months,
      catAgeWeeks: cat.age?.weeks,
      ageMode: cat.age?.mode,
    }),
    foodTypeBeforeKatkin: cat.diet?.current?.type ?? [],
    brandBeforeKatkin: cat.diet?.current?.brand ?? "UNKNOWN",
    fussiness: cat.diet?.eatingHabits ?? "UNKNOWN",
    benefits: [],
    primarySwitchReason: null,
    secondarySwitchReasons: [],
  },
  breeder: null,
});

export const transformStoreDataForCreateAbandonedCartCats = ({
  cats,
  user,
  subscription,
  userId,
  formUsed,
  trackingInfo,
  isShreddedRecipeTestVariant,
}: ITransformStoreDataForCreateFreshCustomerParams): IFreshCreateOrUpdateCatsRequest => {
  const validCats = Object.values(cats).filter((cat) => !cat.softDelete);

  const transformedCats: IFreshCreateOrUpdateCatData[] = validCats.map((cat) => transformCatForAbandonedCart(cat));

  const customerSUFAnswers: IFreshCustomerSUFAnswers = {
    persona: user.persona ?? null,
  };

  return {
    userId: userId ?? "",
    formUsed,
    consentsToMarketing: user.marketingConsent ?? false,
    cats: transformedCats,
    discountCode: subscription.discount.discountCode ?? undefined,
    customerSUFAnswers,
    fbc: trackingInfo?.fbc,
    fbp: trackingInfo?.fbp,
    pageUrl: trackingInfo?.pageUrl,
    ttclid: trackingInfo?.ttclid,
    ttp: trackingInfo?._ttp,
    isShreddedRecipeTestVariant,
  };
};
